import { useEffect, useRef, useState } from "react";
import styles from "./PlatformUnavailable.module.css";

// Shown by DashboardShell when /api/me came back 5xx or the fetch rejected -
// ztc-core is down, the session is probably fine. Before this existed the
// shell treated that as "logged out" and sent the browser to
// /oauth2/authorization/cognito, which during an outage is the ALB's raw 503.
//
// Deliberately the same composition as ztc-frontend/public/error.html and the
// page ClawProxyExceptionHandler renders, so the three read as one product.
// This one lives inside the SPA, so it can do what they cannot: recover in
// place without the customer touching anything.

const RETRY_MS = 15_000;

const STATUS_NAMES: Record<number, string> = {
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

type Props = {
  // null when the request never got a response (offline, DNS, TLS).
  httpStatus: number | null;
};

export const PlatformUnavailable = ({ httpStatus }: Props): JSX.Element => {
  const [attempts, setAttempts] = useState<number>(0);
  const [checking, setChecking] = useState<boolean>(false);
  // A ref, not the `checking` state: the interval below closes over the probe
  // from the first render, so it would read `checking` as false forever. The
  // guard matters precisely in the case this screen exists for - a request
  // that hangs rather than failing fast stacks up a new probe every 15s.
  const inFlight = useRef<boolean>(false);

  const probe = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setChecking(true);
    try {
      const res = await fetch("/api/me", { credentials: "include", cache: "no-store" });
      // Any answer that is not a server error means ztc-core is back. A 401
      // counts: the reload lands on the normal sign-in redirect, which is
      // the correct destination once the platform is actually serving.
      if (res.status < 500) {
        window.location.reload();
        return;
      }
    } catch {
      // Still unreachable. Fall through to the next cycle.
    }
    setAttempts((n) => n + 1);
    setChecking(false);
    inFlight.current = false;
  };

  useEffect(() => {
    const id = window.setInterval(() => {
      void probe();
    }, RETRY_MS);
    return () => window.clearInterval(id);
  }, []);

  const codeLabel = httpStatus !== null ? String(httpStatus) : "offline";
  const codeName =
    httpStatus !== null
      ? (STATUS_NAMES[httpStatus] ?? "Server Error")
      : "No response from the platform";

  return (
    <div className={styles.wrap} role="alert" aria-live="polite">
      <div className={styles.inner}>
        <span className={styles.wordmark} role="img" aria-label="zerotoclaw">
          <span>zero</span>
          <span className={styles.wordmarkBracket}>[</span>
          <span className={styles.wordmarkRed}>to</span>
          <span className={styles.wordmarkBracket}>]</span>
          <span>claw</span>
        </span>

        <div className={styles.code} role="img" aria-label={`HTTP ${codeLabel}`}>
          <span className={styles.bracket} aria-hidden>
            [
          </span>
          <span>{codeLabel}</span>
          <span className={styles.bracket} aria-hidden>
            ]
          </span>
        </div>
        <p className={styles.codeName}>{codeName}</p>

        <h1 className={styles.headline}>We can&apos;t reach the platform right now</h1>
        <p className={styles.lede}>
          This one is on us, not on you. Your instance and its data are untouched, and
          you&apos;re still signed in. We&apos;re usually back within a few minutes.
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primary}
            onClick={() => void probe()}
            disabled={checking}
          >
            {checking ? "Checking..." : "Try again now"}
          </button>
        </div>

        <p className={styles.retryNote}>
          {attempts === 0
            ? "Checking again automatically..."
            : `Still unavailable after ${attempts} ${attempts === 1 ? "check" : "checks"}. Retrying every ${RETRY_MS / 1000}s.`}
        </p>
      </div>
    </div>
  );
};
